import type { ReactNode } from 'react';
import type { Dials, RaceStage } from '@signalvote/shared';
import { RaceStageKind } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { checkWords, cushionWord, drawWord, fmt, heatWord, isPartyPrimary, onePlace, ownYear, pct, winLabel, yearWord, type ReadyWork } from './goalWords';
import type { OwnField, StageDraft } from './stageGoal';

interface Editable {
  text: string;
  own: boolean;
  suffix?: string;
  placeholder?: string;
  error?: string;
  onChange: (text: string) => void;
  /** Absent on a number only the candidate can give. */
  onUseOurs?: () => void;
  /** What to call ours when we have none. */
  noOurs?: string;
}

interface Row {
  op: '' | '×' | '=' | '→' | '+';
  label: string;
  value?: ReactNode;
  edit?: Editable;
  /** Where our number came from, shown until the candidate types over it. */
  source?: string;
}

/**
 * The goal as a ledger, one step per row with its operator in its own column. Rows marked "our number" take the
 * candidate's own; the math then runs through `withOwnNumbers` in `workOut`, and a typed goal through `checkOwnGoal`.
 */
export function GoalMath({ stage, draft, work, advanced, onDraft }: { stage: RaceStage; draft: StageDraft; work: ReadyWork; advanced: boolean; onDraft: (draft: StageDraft) => void }) {
  const { goal, dials, base } = work;
  const manual = !!base.manual;
  const setOwn = (field: OwnField, text: string | undefined) => {
    const own = { ...draft.own };
    if (text === undefined) delete own[field];
    else own[field] = text;
    // Their own ballots are one number, so going back to ours puts the year back on ours too.
    const dialsBack = field === 'usual' && text === undefined ? (({ year: _year, ...rest }) => rest)(draft.dials) : draft.dials;
    onDraft({ ...draft, own, dials: dialsBack });
  };
  const ownEdit = (field: OwnField, ours: string, suffix?: string): Editable => ({
    text: draft.own[field] ?? ours,
    own: draft.own[field] !== undefined,
    suffix,
    error: work.errors[field],
    onChange: (text) => setOwn(field, text),
    onUseOurs: () => setOwn(field, undefined),
  });
  const manualEdit = (field: 'votesLastTime' | 'registered', placeholder?: string): Editable => ({
    text: draft.manual[field],
    own: true,
    placeholder,
    error: work.errors[field],
    onChange: (text) => onDraft({ ...draft, manual: { ...draft.manual, [field]: text } }),
  });

  const baseWord = stage.status === 'Estimated' ? `the ${monthYear(stage.baseElection)} ${stage.kind === RaceStageKind.General ? 'general' : 'primary'}` : '';
  const among = isPartyPrimary(stage.kind) ? `, among voters who took a ${stage.kind === RaceStageKind.RepublicanPrimary ? 'Republican' : 'Democratic'} ballot` : '';
  const { turnout, registered } = work.contest.numbers;
  const { working } = goal;

  const rows: Row[] = [];
  if (manual) rows.push({ op: '', label: 'votes last time', edit: manualEdit('votesLastTime') });
  else rows.push({ op: '', label: ownYear(work) ? 'ballots expected' : `ballots expected in a ${yearWord(work)} year`, edit: ownEdit('usual', fmt(dials.year)), source: `From ${baseWord}${among}.` });
  if (working.draw !== 1) rows.push({ op: '×', label: drawWord(work), value: factor(working.draw) });
  if (working.nudge !== 1) rows.push({ op: '×', label: 'your turnout change', value: factor(working.nudge) });
  if (rows.length > 1) rows.push({ op: '=', label: 'ballots', value: fmt(goal.ballots) });
  if (!manual) {
    const ours = base.numbers.shareVotingInRace;
    rows.push({ op: '×', label: 'vote in this race; the rest skip it', edit: ownEdit('share', String(+(ours * 100).toFixed(1)), '%'), source: `From ${baseWord}: ${pct(ours)} of ballots had a vote in this race.` });
  }
  if (Math.abs(dials.heat - work.defaults.heat) > 0.001) rows.push({ op: '→', label: `with ${heatWord(work)}`, value: pct(goal.votes / Math.max(1, goal.ballots)) });
  if (!manual || rows.length > 1) rows.push({ op: '=', label: 'votes in this race', value: fmt(goal.votes) });
  if (goal.share === 0) rows.push({ op: '→', label: `vote: ${dials.candidates} running, nobody can lose`, value: '1' });
  else {
    rows.push({ op: '×', label: winLabel(work.contest.rule, dials.candidates), value: pct(goal.share, goal.share < 0.3 ? 1 : 0), source: stage.ruleText ?? undefined });
    if (working.cushion !== null)
      rows.push({
        op: '×',
        label: advanced ? cushionWord(dials.cushion) : 'a little extra, for safety',
        value: working.cushion.toFixed(2),
        source: dials.cushion === 3 ? `What most campaign trainers plan for${onePlace(work) ? ': 53% of a two-way race' : ''}.` : undefined,
      });
    else rows.push({ op: '+', label: 'vote, just past it', value: '1' });
  }
  // The goal is theirs once any number behind it is: one they typed or gave, or a dial they moved off ours.
  const anyOwn = Object.keys(draft.own).length > 0;
  const dialMoved = (Object.keys(dials) as (keyof Dials)[]).some((dial) => Math.abs(dials[dial] - work.defaults[dial]) > 0.001);
  const aim = ownEdit('aim', fmt(goal.aim));
  rows.push({ op: '=', label: 'votes to aim for', edit: aim.own ? aim : { ...aim, own: manual || anyOwn || dialMoved, onUseOurs: undefined } });

  const registeredRow: Row = manual
    ? { op: '', label: 'registered voters', edit: manualEdit('registered', 'optional') }
    : base.numbers.registered === null
      ? { op: '', label: 'registered voters', edit: { ...ownEdit('registered', ''), noOurs: 'optional, we don’t have this' } }
      : { op: '', label: 'registered voters', edit: ownEdit('registered', fmt(base.numbers.registered)), source: 'From the voter file.' };
  const check = checkWords(work);

  return (
    <section aria-label="The math" className="max-w-xl rounded-xl border bg-card p-4 text-sm">
      <div className="divide-y">
        {rows.map((row) => (
          <MathRow key={row.label} row={row} />
        ))}
      </div>
      {check && <p className={cn('mt-3 rounded-md px-3 py-2', work.check?.sits === 'Within' ? 'bg-muted' : 'bg-amber-50 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200')}>{check}</p>}
      <div className="mt-3 border-t border-dashed pt-3">
        <MathRow row={registeredRow} />
        <p className="mt-1 text-muted-foreground">
          {registered ? `${pct(turnout.usual / registered)} of them vote in a usual year. ` : manual ? 'Optional. ' : ''}
          This only matters when something above your race on the ballot changes turnout.
        </p>
      </div>
      <p className="mt-3 text-muted-foreground">
        {manual && 'Last time’s votes already count only this race, so nobody skips it. '}
        Type over any number marked “our number” if you have better data.
        {anyOwn && (
          <>
            {' '}
            <button type="button" className="font-semibold text-primary underline-offset-2 hover:underline" onClick={() => onDraft({ ...draft, own: {}, dials: (({ year: _year, ...rest }) => rest)(draft.dials) })}>
              Use all our numbers
            </button>
          </>
        )}
      </p>
    </section>
  );
}

function MathRow({ row }: { row: Row }) {
  const { edit } = row;
  return (
    <div data-row className={cn('grid grid-cols-[1.25rem_6.5rem_1fr] items-start gap-x-3 py-2', row.op === '=' && 'font-semibold')}>
      <span aria-hidden="true" className="pt-1 text-center text-muted-foreground">
        {row.op}
      </span>
      {edit ? (
        <span className={cn('flex items-center justify-end rounded-md border px-2 py-1', edit.own ? 'border-primary text-primary' : 'border-input', edit.error && 'border-destructive')}>
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            aria-label={row.label}
            aria-invalid={edit.error ? true : undefined}
            value={edit.text}
            placeholder={edit.placeholder}
            onFocus={(e) => e.target.select()}
            onChange={(e) => edit.onChange(e.target.value)}
            className="w-full bg-transparent text-right font-semibold tabular-nums outline-none placeholder:font-normal placeholder:text-muted-foreground"
          />
          {edit.suffix}
        </span>
      ) : (
        <span className="pt-1 text-right font-semibold tabular-nums">{row.value}</span>
      )}
      <span className="pt-1">
        <span className="block">{row.label}</span>
        {edit &&
          (edit.own ? (
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">
              <span>your number</span>
              {edit.onUseOurs && (
                <>
                  {' · '}
                  <button type="button" className="normal-case underline" onClick={edit.onUseOurs}>
                    use ours
                  </button>
                </>
              )}
            </span>
          ) : (
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{edit.noOurs ?? 'our number'}</span>
          ))}
        {row.source && !edit?.own && <span className="block text-xs text-muted-foreground">{row.source}</span>}
        {edit?.error && <span className="block text-xs text-destructive">{edit.error}</span>}
      </span>
    </div>
  );
}

const factor = (f: number) => f.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');

const MONTH_YEAR = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' });

function monthYear(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return MONTH_YEAR.format(Date.UTC(y, m - 1, d));
}
